<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { fromUnixTime, subDays, format } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'shared/helpers/DateHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';
import ReportHeader from 'dashboard/routes/dashboard/settings/reports/components/ReportHeader.vue';
import kanbanModule from 'kanban/store/modules/kanban';
import {
  REPORT_TABS,
  REPORT_TAB_ORDER,
  ALL_BOARDS_VALUE,
  ALL_AGENTS_VALUE,
  ALL_INBOXES_VALUE,
  EXPORTABLE_REPORTS,
  DEFAULT_TIMESERIES_METRIC,
  DEFAULT_GROUP_BY,
  DEFAULT_CFD_GROUP_BY,
  DEFAULT_RANGE_DAYS,
} from 'kanban/constants/reports';
import { useKanbanReports } from 'kanban/composables/useKanbanReports';
import ReportsAPI from 'kanban/api/reports';
import KanbanSummaryCards from 'kanban/components/reports/KanbanSummaryCards.vue';
import KanbanThroughputChart from 'kanban/components/reports/KanbanThroughputChart.vue';
import KanbanAgentLeaderboard from 'kanban/components/reports/KanbanAgentLeaderboard.vue';
import KanbanFunnelChart from 'kanban/components/reports/KanbanFunnelChart.vue';
import KanbanCfdChart from 'kanban/components/reports/KanbanCfdChart.vue';
import KanbanAgingTable from 'kanban/components/reports/KanbanAgingTable.vue';
import KanbanStageDurations from 'kanban/components/reports/KanbanStageDurations.vue';
import KanbanBoardRequiredState from 'kanban/components/reports/KanbanBoardRequiredState.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');

const isKanbanEnabled = computed(
  () => isCloudFeatureEnabled(FEATURE_FLAGS.KANBAN)
);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

if (!store.hasModule('kanban')) {
  store.registerModule('kanban', kanbanModule);
}
const boards = computed(() => store.state.kanban.boards);

// Filters (board + agent + period), seeded from the URL for refresh/share.
const selectedBoardId = ref(
  route.query.board_id ? String(route.query.board_id) : ALL_BOARDS_VALUE
);
const selectedAgentId = ref(
  route.query.agent_id ? String(route.query.agent_id) : ALL_AGENTS_VALUE
);
const selectedInboxId = ref(
  route.query.inbox_id ? String(route.query.inbox_id) : ALL_INBOXES_VALUE
);
const activeTab = ref(
  REPORT_TAB_ORDER.includes(route.query.tab)
    ? route.query.tab
    : REPORT_TABS.OVERVIEW
);
const rangeType = ref(route.query.range_type || DATE_RANGE_TYPES.LAST_30_DAYS);
// Only honor query timestamps that parse to finite numbers so a malformed since/until in the
// URL falls back to the default window instead of yielding an Invalid Date.
const dateFromQuery = (value, fallback) => {
  const timestamp = Number(value);
  return value && Number.isFinite(timestamp)
    ? fromUnixTime(timestamp)
    : fallback;
};
const dateRange = ref([
  dateFromQuery(route.query.since, subDays(new Date(), DEFAULT_RANGE_DAYS - 1)),
  dateFromQuery(route.query.until, new Date()),
]);

const metric = ref(DEFAULT_TIMESERIES_METRIC);
const groupBy = ref(DEFAULT_GROUP_BY);
const cfdGroupBy = ref(DEFAULT_CFD_GROUP_BY);
const agingPage = ref(1);

const since = computed(() => getUnixStartOfDay(dateRange.value[0]));
const until = computed(() => getUnixEndOfDay(dateRange.value[1]));

const boardId = computed(() =>
  selectedBoardId.value === ALL_BOARDS_VALUE
    ? null
    : Number(selectedBoardId.value)
);
const agentId = computed(() =>
  selectedAgentId.value === ALL_AGENTS_VALUE
    ? null
    : Number(selectedAgentId.value)
);
const inboxId = computed(() =>
  selectedInboxId.value === ALL_INBOXES_VALUE
    ? null
    : Number(selectedInboxId.value)
);
const selectedBoard = computed(() =>
  boards.value.find(board => String(board.id) === selectedBoardId.value)
);
const selectedAgent = computed(() =>
  agents.value.find(agent => String(agent.id) === selectedAgentId.value)
);

const baseParams = computed(() => {
  const params = { since: since.value, until: until.value };
  if (boardId.value) params.board_id = boardId.value;
  if (agentId.value) params.agent_id = agentId.value;
  if (inboxId.value) params.inbox_id = inboxId.value;
  return params;
});

const boardOptions = computed(() => [
  { label: t('KANBAN.REPORTS.ALL_BOARDS'), value: ALL_BOARDS_VALUE },
  ...boards.value.map(board => ({
    label: board.name,
    value: String(board.id),
  })),
]);
const selectedBoardLabel = computed(() =>
  selectedBoardId.value === ALL_BOARDS_VALUE
    ? t('KANBAN.REPORTS.ALL_BOARDS')
    : selectedBoard.value?.name || t('KANBAN.REPORTS.ALL_BOARDS')
);

const agentOptions = computed(() => [
  { label: t('KANBAN.REPORTS.ALL_AGENTS'), value: ALL_AGENTS_VALUE },
  ...agents.value.map(agent => ({
    label: agent.name,
    value: String(agent.id),
  })),
]);
const selectedAgentLabel = computed(() =>
  selectedAgentId.value === ALL_AGENTS_VALUE
    ? t('KANBAN.REPORTS.ALL_AGENTS')
    : selectedAgent.value?.name || t('KANBAN.REPORTS.ALL_AGENTS')
);

// Inbox options come from the inboxes assigned to funnels (already in the boards payload),
// so the list stays relevant to Kanban without an extra request.
const inboxesById = computed(() => {
  const map = new Map();
  boards.value.forEach(board =>
    (board.assigned_inboxes || []).forEach(inbox =>
      map.set(String(inbox.id), inbox)
    )
  );
  return map;
});
const inboxOptions = computed(() => [
  { label: t('KANBAN.REPORTS.ALL_INBOXES'), value: ALL_INBOXES_VALUE },
  ...[...inboxesById.value.values()].map(inbox => ({
    label: inbox.name,
    value: String(inbox.id),
  })),
]);
const selectedInboxLabel = computed(() =>
  selectedInboxId.value === ALL_INBOXES_VALUE
    ? t('KANBAN.REPORTS.ALL_INBOXES')
    : inboxesById.value.get(selectedInboxId.value)?.name ||
      t('KANBAN.REPORTS.ALL_INBOXES')
);

// Once the option lists are loaded, drop any board/agent/inbox id seeded from the URL that
// no longer exists (deleted or unauthorized), so a stale id can't leak into the API params.
const normalizeFilters = () => {
  if (
    selectedBoardId.value !== ALL_BOARDS_VALUE &&
    !boards.value.some(board => String(board.id) === selectedBoardId.value)
  ) {
    selectedBoardId.value = ALL_BOARDS_VALUE;
  }
  if (
    selectedAgentId.value !== ALL_AGENTS_VALUE &&
    !agents.value.some(agent => String(agent.id) === selectedAgentId.value)
  ) {
    selectedAgentId.value = ALL_AGENTS_VALUE;
  }
  if (
    selectedInboxId.value !== ALL_INBOXES_VALUE &&
    !inboxesById.value.has(selectedInboxId.value)
  ) {
    selectedInboxId.value = ALL_INBOXES_VALUE;
  }
};

// CSV export options for the active tab; board-scoped tabs need a board first.
const exportOptions = computed(() =>
  (EXPORTABLE_REPORTS[activeTab.value] || []).map(item => ({
    value: item.report,
    label: t(`KANBAN.REPORTS.${item.labelKey}`),
  }))
);

const tabItems = computed(() =>
  REPORT_TAB_ORDER.map(key => ({
    key,
    label: t(`KANBAN.REPORTS.TABS.${key.toUpperCase()}`),
  }))
);
const activeTabIndex = computed(() =>
  REPORT_TAB_ORDER.indexOf(activeTab.value)
);
const requiresBoard = computed(() => activeTab.value !== REPORT_TABS.OVERVIEW);
const showBoardRequired = computed(() => requiresBoard.value && !boardId.value);
const canExport = computed(
  () => exportOptions.value.length > 0 && !showBoardRequired.value
);

const {
  reports,
  fetchSummary,
  fetchTimeseries,
  fetchAgentPerformance,
  fetchFunnel,
  fetchCfd,
  fetchAging,
  fetchStageDurations,
} = useKanbanReports();

const reloadSummary = () => fetchSummary(baseParams.value);
const reloadTimeseries = () =>
  fetchTimeseries({
    ...baseParams.value,
    metric: metric.value,
    group_by: groupBy.value,
  });
const reloadAgentPerformance = () => fetchAgentPerformance(baseParams.value);
const reloadFunnel = () => fetchFunnel(baseParams.value);
const reloadCfd = () =>
  fetchCfd({ ...baseParams.value, group_by: cfdGroupBy.value });
const reloadAging = () =>
  fetchAging({ ...baseParams.value, page: agingPage.value });
const reloadStageDurations = () => fetchStageDurations(baseParams.value);

// Lazy-load: only fetch what the active tab needs. No-op behind the paywall so we never
// fire report requests the user can't see.
const loadActiveTab = () => {
  if (!isKanbanEnabled.value) return;
  if (activeTab.value === REPORT_TABS.OVERVIEW) {
    reloadSummary();
    reloadTimeseries();
    reloadAgentPerformance();
  } else if (activeTab.value === REPORT_TABS.FUNNEL && boardId.value) {
    reloadFunnel();
  } else if (activeTab.value === REPORT_TABS.FLOW && boardId.value) {
    reloadCfd();
    reloadAging();
    reloadStageDurations();
  }
};

const onBoardSelect = value => {
  selectedBoardId.value = value;
};
const onAgentSelect = value => {
  selectedAgentId.value = value;
};
const onInboxSelect = value => {
  selectedInboxId.value = value;
};
const onTabChanged = tab => {
  activeTab.value = tab.key;
};
const onDateRangeChanged = ([start, end, type]) => {
  dateRange.value = [start, end];
  rangeType.value = type;
};
const onMetricChange = value => {
  metric.value = value;
  reloadTimeseries();
};
const onGroupByChange = value => {
  groupBy.value = value;
  reloadTimeseries();
};
const onCfdGroupByChange = value => {
  cfdGroupBy.value = value;
  reloadCfd();
};
const onAgingPage = page => {
  agingPage.value = page;
  reloadAging();
};

// Extra params per report so the CSV matches what's on screen (granularity, page).
const downloadParams = report => {
  const params = { ...baseParams.value };
  if (report === 'timeseries') {
    params.metric = metric.value;
    params.group_by = groupBy.value;
  } else if (report === 'cfd') {
    params.group_by = cfdGroupBy.value;
  } else if (report === 'aging') {
    params.page = agingPage.value;
  }
  return params;
};
const onExport = async report => {
  try {
    const csv = await ReportsAPI.download(report, downloadParams(report));
    downloadCsvFile(
      `kanban-${report}-${format(new Date(), 'dd-MM-yyyy')}.csv`,
      csv
    );
  } catch {
    useAlert(t('KANBAN.REPORTS.EXPORT.ERROR'));
  }
};

watch([boardId, agentId, inboxId, since, until], () => {
  agingPage.value = 1;
  loadActiveTab();
});
watch(activeTab, () => loadActiveTab());

// Keep board/agent/inbox/tab/period in the URL so the report is shareable and survives refresh.
watch(
  [
    selectedBoardId,
    selectedAgentId,
    selectedInboxId,
    activeTab,
    rangeType,
    since,
    until,
  ],
  () => {
    const query = {
      tab: activeTab.value,
      range_type: rangeType.value,
      since: String(since.value),
      until: String(until.value),
    };
    if (selectedBoardId.value !== ALL_BOARDS_VALUE) {
      query.board_id = selectedBoardId.value;
    }
    if (selectedAgentId.value !== ALL_AGENTS_VALUE) {
      query.agent_id = selectedAgentId.value;
    }
    if (selectedInboxId.value !== ALL_INBOXES_VALUE) {
      query.inbox_id = selectedInboxId.value;
    }
    router.replace({ query });
  }
);

// Skip board/agent/report fetches when the local account feature is disabled.
const initialize = async () => {
  if (!isKanbanEnabled.value) return;
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('kanban/fetchBoards'),
  ]);
  normalizeFilters();
  loadActiveTab();
};
watch(isKanbanEnabled, enabled => {
  if (enabled) initialize();
});
onMounted(initialize);
</script>

<template>
  <div class="font-inter">
    <div
      v-if="!isKanbanEnabled"
      class="flex items-center justify-center min-h-[400px] py-12"
    >
      <BasePaywallModal
        feature-prefix="KANBAN"
        i18n-key="LOCAL_PAYWALL"
        :is-super-admin="isSuperAdmin"
        super-admin-url="/super_admin/settings"
      />
    </div>

    <div v-else>
      <ReportHeader :header-title="t('KANBAN.REPORTS.TITLE')">
        <SelectMenu
          v-if="canExport"
          model-value=""
          :options="exportOptions"
          :label="t('KANBAN.REPORTS.EXPORT.LABEL')"
          icon="i-lucide-download"
          sub-menu-position="bottom"
          max-width="max-w-56"
          @update:model-value="onExport"
        />
      </ReportHeader>

      <div class="flex flex-col gap-6 pb-6">
        <div class="flex flex-col w-full gap-3 lg:flex-row lg:items-center">
          <WootDatePicker
            v-model:date-range="dateRange"
            v-model:range-type="rangeType"
            @date-range-changed="onDateRangeChanged"
          />
          <div
            class="flex flex-col gap-3 lg:flex-row lg:items-center lg:ms-auto"
          >
            <SelectMenu
              :model-value="selectedBoardId"
              :options="boardOptions"
              :label="selectedBoardLabel"
              icon="i-lucide-kanban"
              show-avatar
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onBoardSelect"
            />
            <SelectMenu
              :model-value="selectedAgentId"
              :options="agentOptions"
              :label="selectedAgentLabel"
              icon="i-lucide-user"
              show-avatar
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onAgentSelect"
            />
            <SelectMenu
              v-if="inboxOptions.length > 1"
              :model-value="selectedInboxId"
              :options="inboxOptions"
              :label="selectedInboxLabel"
              icon="i-lucide-inbox"
              sub-menu-position="bottom"
              max-width="max-w-56"
              @update:model-value="onInboxSelect"
            />
          </div>
        </div>

        <TabBar
          :tabs="tabItems"
          :initial-active-tab="activeTabIndex"
          @tab-changed="onTabChanged"
        />

        <div class="flex flex-col gap-6">
          <template v-if="activeTab === REPORT_TABS.OVERVIEW">
            <KanbanSummaryCards
              :data="reports.summary.data"
              :is-loading="reports.summary.isLoading"
              :error="reports.summary.error"
              :locale="locale"
              @retry="reloadSummary"
            />
            <KanbanThroughputChart
              :data="reports.timeseries.data"
              :is-loading="reports.timeseries.isLoading"
              :error="reports.timeseries.error"
              :metric="metric"
              :group-by="groupBy"
              :locale="locale"
              @retry="reloadTimeseries"
              @update:metric="onMetricChange"
              @update:group-by="onGroupByChange"
            />
            <KanbanAgentLeaderboard
              :data="reports.agentPerformance.data"
              :is-loading="reports.agentPerformance.isLoading"
              :error="reports.agentPerformance.error"
              :locale="locale"
              @retry="reloadAgentPerformance"
            />
          </template>

          <template v-else-if="activeTab === REPORT_TABS.FUNNEL">
            <KanbanBoardRequiredState
              v-if="showBoardRequired"
              :boards="boards"
              @select="onBoardSelect(String($event))"
            />
            <KanbanFunnelChart
              v-else
              :data="reports.funnel.data"
              :is-loading="reports.funnel.isLoading"
              :error="reports.funnel.error"
              :locale="locale"
              @retry="reloadFunnel"
            />
          </template>

          <template v-else-if="activeTab === REPORT_TABS.FLOW">
            <KanbanBoardRequiredState
              v-if="showBoardRequired"
              :boards="boards"
              @select="onBoardSelect(String($event))"
            />
            <template v-else>
              <KanbanCfdChart
                :data="reports.cfd.data"
                :is-loading="reports.cfd.isLoading"
                :error="reports.cfd.error"
                :group-by="cfdGroupBy"
                :locale="locale"
                @retry="reloadCfd"
                @update:group-by="onCfdGroupByChange"
              />
              <KanbanAgingTable
                :data="reports.aging.data"
                :is-loading="reports.aging.isLoading"
                :error="reports.aging.error"
                :locale="locale"
                @retry="reloadAging"
                @update:page="onAgingPage"
              />
              <KanbanStageDurations
                :data="reports.stageDurations.data || []"
                :is-loading="reports.stageDurations.isLoading"
                :error="reports.stageDurations.error"
                :locale="locale"
                @retry="reloadStageDurations"
              />
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
